<template>
  <div
    :class="['email-component', 'text-component', { selected, editing }]"
    :style="computedStyles"
    @click="handleClick"
  >
    <!-- Rich Text Editor Mode -->
    <div
      v-if="editing"
      class="rich-text-editor"
    >
      <div class="editor-toolbar">
        <!-- Text Style Dropdown -->
        <select
          class="toolbar-select style-select"
          @change="applyTextStyle"
          title="Text Style"
        >
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="h4">Heading 4</option>
          <option value="h5">Heading 5</option>
          <option value="h6">Heading 6</option>
        </select>

        <div class="toolbar-divider"></div>

        <!-- Font Family Dropdown -->
        <select
          class="toolbar-select font-select"
          :value="currentFontFamily"
          @change="applyFontFamily"
          title="Font Family"
        >
          <option value="Arial, sans-serif">Arial</option>
          <option value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</option>
          <option value="Georgia, serif">Georgia</option>
          <option value="'Times New Roman', Times, serif">Times New Roman</option>
          <option value="'Courier New', Courier, monospace">Courier New</option>
          <option value="Verdana, sans-serif">Verdana</option>
          <option value="Tahoma, sans-serif">Tahoma</option>
          <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
          <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
        </select>

        <!-- Font Size Dropdown -->
        <div class="toolbar-control-group">
          <i class="toolbar-icon fas fa-text-height" title="Font size"></i>

          <select
            class="toolbar-select size-select"
            :value="currentFontSize"
            @mousedown="saveSelection"
            @change="applyFontSize($event)"
            title="Font size"
          >
            <option value="10px">10px</option>
            <option value="12px">12px</option>
            <option value="14px">14px</option>
            <option value="16px">16px</option>
            <option value="18px">18px</option>
            <option value="20px">20px</option>
            <option value="24px">24px</option>
            <option value="28px">28px</option>
            <option value="32px">32px</option>
            <option value="36px">36px</option>
            <option value="48px">48px</option>
          </select>
        </div>

        <i class="toolbar-icon fas fa-align-justify" title="Line height"></i>
        <select
          class="toolbar-select line-height-select"
          :value="normalizeLineHeight(currentLineHeight)"
          @change="applyLineHeight($event)"
          title="Line height"
        >
          <option value="1">1.0</option>
          <option value="1.15">1.15</option>
          <option value="1.25">1.25</option>
          <option value="1.5">1.5</option>
          <option value="1.75">1.75</option>
          <option value="2">2.0</option>
          <option value="2.5">2.5</option>
          <option value="3">3.0</option>
        </select>

        <div class="toolbar-divider"></div>

        <!-- Text Formatting -->
        <button
          class="toolbar-btn"
          @click="execCommand('bold')"
          :class="{ active: isCommandActive('bold') }"
          title="Bold (Ctrl+B)"
        >
          <i class="fas fa-bold"></i>
        </button>
        <button
          class="toolbar-btn"
          @click="execCommand('italic')"
          :class="{ active: isCommandActive('italic') }"
          title="Italic (Ctrl+I)"
        >
          <i class="fas fa-italic"></i>
        </button>
        <button
          class="toolbar-btn"
          @click="execCommand('underline')"
          :class="{ active: isCommandActive('underline') }"
          title="Underline (Ctrl+U)"
        >
          <i class="fas fa-underline"></i>
        </button>
        <button
          class="toolbar-btn"
          @click="execCommand('strikeThrough')"
          :class="{ active: isCommandActive('strikeThrough') }"
          title="Strikethrough"
        >
          <i class="fas fa-strikethrough"></i>
        </button>

        <div class="toolbar-divider"></div>

        <!-- Text Color & Highlight -->
        <div class="toolbar-group">
          <div class="color-button-wrapper">
            <button
              class="toolbar-btn color-btn"
              @click="showColorPicker = !showColorPicker; showHighlightPicker = false"
              title="Text Color"
            >
              <i class="fas fa-font"></i>
              <div class="color-indicator" :style="{ backgroundColor: currentTextColor }"></div>
            </button>
            <!-- Color Picker Dropdown -->
            <div v-if="showColorPicker" class="color-picker-dropdown">
              <div class="color-presets">
                <button
                  v-for="color in textColorPresets"
                  :key="color"
                  class="color-preset"
                  :style="{ backgroundColor: color }"
                  @click="applyTextColor(color)"
                  :title="color"
                >
                  <i v-if="currentTextColor === color" class="fas fa-check"></i>
                </button>
              </div>
            </div>
          </div>
          <div class="color-button-wrapper">
            <button
              class="toolbar-btn highlight-btn"
              @click="showHighlightPicker = !showHighlightPicker; showColorPicker = false"
              title="Highlight Color"
            >
              <i class="tim-icons icon-pencil"></i>
              <div class="color-indicator" :style="{ backgroundColor: currentHighlightColor }"></div>
            </button>
            <!-- Highlight Picker Dropdown - NOW INSIDE THE WRAPPER -->
            <div v-if="showHighlightPicker" class="highlight-picker-dropdown">
              <div class="color-presets">
                <button
                  v-for="color in highlightColorPresets"
                  :key="color"
                  class="color-preset"
                  :style="{ backgroundColor: color }"
                  @click="applyHighlightColor(color)"
                  :title="color"
                >
                  <i v-if="currentHighlightColor === color" class="fas fa-check"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="toolbar-divider"></div>

        <!-- Lists & Indentation -->
        <button
          class="toolbar-btn"
          @click="toggleBulletList"
          :class="{ active: isListActive('ul') }"
          title="Bullet List"
        >
          <i class="fas fa-list-ul"></i>
        </button>
        <button
          class="toolbar-btn"
          @click="toggleNumberedList"
          :class="{ active: isListActive('ol') }"
          title="Numbered List"
        >
          <i class="fas fa-list-ol"></i>
        </button>
        <button
          class="toolbar-btn"
          @click="execCommand('outdent')"
          title="Decrease Indent"
        >
          <i class="fas fa-outdent"></i>
        </button>
        <button
          class="toolbar-btn"
          @click="execCommand('indent')"
          title="Increase Indent"
        >
          <i class="fas fa-indent"></i>
        </button>

        <div class="toolbar-divider"></div>

        <!-- Text Alignment -->
        <button
          class="toolbar-btn"
          @click="execCommand('justifyLeft')"
          :class="{ active: isCommandActive('justifyLeft') }"
          title="Align Left"
        >
          <i class="fas fa-align-left"></i>
        </button>
        <button
          class="toolbar-btn"
          @click="execCommand('justifyCenter')"
          :class="{ active: isCommandActive('justifyCenter') }"
          title="Align Center"
        >
          <i class="fas fa-align-center"></i>
        </button>
        <button
          class="toolbar-btn"
          @click="execCommand('justifyRight')"
          :class="{ active: isCommandActive('justifyRight') }"
          title="Align Right"
        >
          <i class="fas fa-align-right"></i>
        </button>
        <button
          class="toolbar-btn"
          @click="execCommand('justifyFull')"
          :class="{ active: isCommandActive('justifyFull') }"
          title="Justify"
        >
          <i class="fas fa-align-justify"></i>
        </button>

        <div class="toolbar-divider"></div>

        <!-- Links -->
        <button
          class="toolbar-btn"
          @click="toggleLink"
          :class="{ active: isCommandActive('createLink') }"
          title="Insert/Edit Link (Ctrl+K)"
        >
          <i class="fas fa-link"></i>
        </button>
        <button
          class="toolbar-btn"
          @click="removeLink"
          title="Remove Link"
        >
          <i class="fas fa-unlink"></i>
        </button>

        <div class="toolbar-divider"></div>

        <!-- Clear Formatting -->
        <button
          class="toolbar-btn"
          @click="execCommand('removeFormat')"
          title="Clear Formatting"
        >
          <i class="fas fa-eraser"></i>
        </button>
      </div>

      <!-- Editable Content -->
      <div
        ref="editor"
        class="editor-content"
        contenteditable="true"
        @input="handleInput"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @focus="handleFocus"
        @blur="handleBlur"
      ></div>
    </div>

    <!-- Display Mode -->
    <div
      v-else
      class="component-content"
      v-html="displayContent"
    ></div>

    <!-- Link Modal -->
    <modal :show.sync="modals.showLinkModal"
           body-classes="p-0"
           modal-classes="modal-dialog-centered">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <h4>{{ editingLink ? 'Edit link' : 'Insert link' }}</h4>
        <div class="form-group">
          <label class="text-white opacity-7">Link text:</label>
          <base-input
            type="text"
            v-model="linkData.text"
            placeholder="Link text"
          ></base-input>
        </div>
        <div class="form-group">
          <label class="text-white opacity-7">URL:</label>
          <base-input
            type="url"
            v-model="linkData.url"
            placeholder="https://example.com"
          ></base-input>
        </div>
        <div class="form-group">
          <label class="text-white opacity-7"></label>
          <base-checkbox v-model="linkData.newTab">Open in new tab</base-checkbox>
        </div>
        <div class="row">
          <div class="col text-center">
            <base-button type="default" @click="closeLinkModal">Cancel</base-button>
            <base-button class="ml-2" type="primary" @click="insertLink">{{ editingLink ? 'Update' : 'Insert' }}</base-button>
          </div>
        </div>
      </card>
    </modal>
  </div>
</template>

<script>
export default {
  name: 'TextComponent',
  props: ['componentData', 'selected'],
  data() {
    return {
      editing: false,
      showColorPicker: false,
      showHighlightPicker: false,
      showLinkModal: false,
      editingLink: false,
      currentTextColor: '#333333',
      currentHighlightColor: 'transparent',
      currentFontFamily: 'Arial, sans-serif',
      currentFontSize: '16px',
      currentLineHeight: '1.5',
      linkData: {
        text: '',
        url: '',
        newTab: true
      },
      modals: {
        showLinkModal: false
      },
      savedSelection: null,
      isUpdating: false,

      // Color presets for text
      textColorPresets: [
        '#000000', '#333333', '#666666', '#999999', '#CCCCCC',
        '#FF0000', '#FF6600', '#FFCC00', '#00FF00', '#0066CC', '#9900CC',
        '#FF3366', '#FF9933', '#FFFF33', '#33FF33', '#3366FF', '#CC33FF',
        '#990000', '#CC6600', '#999900', '#006600', '#003399', '#660099',
        '#FFFFFF'
      ],

      // Highlight color presets
      highlightColorPresets: [
        'transparent', '#FFFF00', '#00FF00', '#00FFFF', '#FF00FF',
        '#FFA500', '#FFE4B5', '#98FB98', '#87CEEB', '#DDA0DD',
        '#F0E68C', '#E0FFFF', '#FFDAB9', '#D3D3D3', '#FFB6C1'
      ]
    }
  },
  computed: {
    computedStyles() {
      const styles = this.componentData.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor
        ? 'transparent'
        : styles.backgroundColor
      const padding = styles.padding || '0'

      return {
        fontFamily: styles.fontFamily || 'Arial, sans-serif',
        fontSize: (styles.fontSize || '16') + 'px',
        fontWeight: styles.fontWeight || '400',
        lineHeight: styles.lineHeight || '1.5',
        color: styles.color || '#333333',
        textAlign: styles.textAlign || 'left',
        marginTop: (styles.marginTop || '16') + 'px',
        marginBottom: (styles.marginBottom || '16') + 'px',
        backgroundColor: backgroundColor,  // Add this
        borderRadius: backgroundColor !== 'transparent' ? '4px' : '0',
        minHeight: '20px',
        position: 'relative'
      }
    },
    displayContent() {
      return this.componentData.content || '<p>Click to edit text</p>'
    }
  },
  watch: {
    selected(newVal) {
      if (newVal && !this.editing) {
        this.startEditing()
      } else if (!newVal && this.editing) {
        this.finishEditing()
      }
    }
  },
  methods: {
    normalizeFontFamily(value) {
      if (!value) return 'Arial, sans-serif'

      // Remove extra spaces and normalize quotes
      let normalized = value.trim()

      // Remove all quotes first for comparison
      const unquoted = normalized.replace(/['"]/g, '')

      // Map of common font variations to our dropdown values
      const fontMap = {
        'Arial, sans-serif': 'Arial, sans-serif',
        'Arial,sans-serif': 'Arial, sans-serif',
        'Helvetica Neue, Helvetica, sans-serif': "'Helvetica Neue', Helvetica, sans-serif",
        'Helvetica, Arial, sans-serif': "'Helvetica Neue', Helvetica, sans-serif",
        'Helvetica,Arial,sans-serif': "'Helvetica Neue', Helvetica, sans-serif",
        'Georgia, serif': 'Georgia, serif',
        'Georgia,serif': 'Georgia, serif',
        'Times New Roman, Times, serif': "'Times New Roman', Times, serif",
        'Times New Roman,Times,serif': "'Times New Roman', Times, serif",
        'Courier New, Courier, monospace': "'Courier New', Courier, monospace",
        'Courier New,Courier,monospace': "'Courier New', Courier, monospace",
        'Verdana, sans-serif': 'Verdana, sans-serif',
        'Verdana,sans-serif': 'Verdana, sans-serif',
        'Tahoma, sans-serif': 'Tahoma, sans-serif',
        'Tahoma,sans-serif': 'Tahoma, sans-serif',
        'Trebuchet MS, sans-serif': "'Trebuchet MS', sans-serif",
        'Trebuchet MS,sans-serif': "'Trebuchet MS', sans-serif",
        'Comic Sans MS, cursive': "'Comic Sans MS', cursive",
        'Comic Sans MS,cursive': "'Comic Sans MS', cursive"
      }

      // Check unquoted version
      if (fontMap[unquoted]) {
        return fontMap[unquoted]
      }

      // Special handling for Helvetica - browsers often return it differently
      if (unquoted.toLowerCase().includes('helvetica')) {
        return "'Helvetica Neue', Helvetica, sans-serif"
      }

      // Check if the value itself is already valid (exact match)
      const validFonts = [
        'Arial, sans-serif',
        "'Helvetica Neue', Helvetica, sans-serif",
        'Georgia, serif',
        "'Times New Roman', Times, serif",
        "'Courier New', Courier, monospace",
        'Verdana, sans-serif',
        'Tahoma, sans-serif',
        "'Trebuchet MS', sans-serif",
        "'Comic Sans MS', cursive"
      ]

      // Try exact match first
      if (validFonts.includes(normalized)) {
        return normalized
      }

      // Try matching by removing quotes from both sides
      for (const validFont of validFonts) {
        if (validFont.replace(/['"]/g, '') === unquoted) {
          return validFont
        }
      }

      // Default fallback
      return 'Arial, sans-serif'
    },

    normalizeLineHeight(value) {
      // Convert to string and remove any trailing zeros after decimal
      const normalized = parseFloat(value).toString()

      // Check if this matches one of our option values
      const validOptions = ['1', '1.15', '1.25', '1.5', '1.75', '2', '2.5', '3']

      // Find the closest matching option
      if (validOptions.includes(normalized)) {
        return normalized
      }

      // If not exact match, find closest
      const numValue = parseFloat(normalized)
      let closest = '1.5' // default
      let minDiff = Infinity

      for (const option of validOptions) {
        const diff = Math.abs(parseFloat(option) - numValue)
        if (diff < minDiff) {
          minDiff = diff
          closest = option
        }
      }

      return closest
    },
    applyLineHeight(event) {
      const lineHeight = event.target.value

      // Get fresh selection from editor
      this.$refs.editor.focus()
      const selection = window.getSelection()

      if (!selection.rangeCount) {
        // If no selection, apply to entire editor content
        this.$refs.editor.style.lineHeight = lineHeight
        this.componentData.styles.lineHeight = lineHeight
        this.currentLineHeight = lineHeight
        this.handleInput({ target: this.$refs.editor })
        return
      }

      const range = selection.getRangeAt(0)

      // Find the parent block element
      let node = range.commonAncestorContainer

      // If the node is the editor itself, apply to all children
      if (node === this.$refs.editor) {
        this.$refs.editor.style.lineHeight = lineHeight
        // Also apply to all direct children
        const children = this.$refs.editor.children
        for (let child of children) {
          child.style.lineHeight = lineHeight
        }
      } else {
        if (node.nodeType === 3) node = node.parentElement

        let parentBlock = node
        while (parentBlock && parentBlock !== this.$refs.editor) {
          const display = window.getComputedStyle(parentBlock).display
          if (display === 'block' || parentBlock.tagName.match(/^(P|DIV|H[1-6]|LI)$/)) {
            break
          }
          parentBlock = parentBlock.parentElement
        }

        // Apply line height to the parent block
        if (parentBlock && parentBlock !== this.$refs.editor) {
          parentBlock.style.lineHeight = lineHeight
        }
      }

      // Update the component styles and current value
      this.componentData.styles.lineHeight = lineHeight
      this.currentLineHeight = lineHeight
      this.handleInput({ target: this.$refs.editor })
    },
    saveSelection() {
      const selection = window.getSelection()
      if (selection.rangeCount > 0) {
        this.savedSelection = selection.getRangeAt(0).cloneRange()
      }
    },
    handleClick(event) {
      if (!this.editing) {
        this.$emit('click', event)
      }
    },

    startEditing() {
      this.editing = true

      // Initialize content if empty
      if (!this.componentData.content || this.componentData.content.trim() === '') {
        this.componentData.content = '<p>Start typing...</p>'
      }
      if (!this.componentData.styles.lineHeight) {
        this.componentData.styles.lineHeight = '1.5'
      }
      if (!this.componentData.styles.fontFamily) {
        this.componentData.styles.fontFamily = 'Arial, sans-serif'
      }

      this.$nextTick(() => {
        if (this.$refs.editor) {
          // Set initial content
          this.$refs.editor.innerHTML = this.componentData.content
          this.$refs.editor.style.lineHeight = this.componentData.styles.lineHeight || '1.5'
          this.$refs.editor.style.fontFamily = this.componentData.styles.fontFamily || 'Arial, sans-serif'

          // Focus and select all
          this.$refs.editor.focus()
          const range = document.createRange()
          const sel = window.getSelection()
          range.selectNodeContents(this.$refs.editor)
          sel.removeAllRanges()
          sel.addRange(range)

          this.currentLineHeight = this.componentData.styles.lineHeight || '1.5'
          this.currentFontFamily = this.normalizeFontFamily(this.componentData.styles.fontFamily || 'Arial, sans-serif')

          // Update toolbar state
          this.updateToolbarState()
        }
      })
    },

    finishEditing() {
      if (this.$refs.editor) {
        // Clean up any problematic styles before saving
        this.cleanupContent()
        this.componentData.content = this.$refs.editor.innerHTML
        this.$emit('update')
      }
      this.editing = false
      this.showColorPicker = false
      this.showHighlightPicker = false
    },

    cleanupContent() {
      if (!this.$refs.editor) return

      // Remove any white text color from lists
      const lists = this.$refs.editor.querySelectorAll('ul, ol, li')
      lists.forEach(element => {
        // Remove inline color styles that might make text invisible
        if (element.style.color === 'white' || element.style.color === '#ffffff' || element.style.color === 'rgb(255, 255, 255)') {
          element.style.color = ''
        }
      })

      // Ensure lists maintain proper structure
      const allLists = this.$refs.editor.querySelectorAll('ul, ol')
      allLists.forEach(list => {
        // Ensure list has proper margin and padding
        if (!list.style.marginLeft && !list.style.paddingLeft) {
          list.style.paddingLeft = '20px'
        }
      })
    },

    handleInput(event) {
      if (this.isUpdating) return

      if (this.inputTimeout) {
        clearTimeout(this.inputTimeout)
      }

      this.inputTimeout = setTimeout(() => {
        this.isUpdating = true
        this.cleanupContent() // Clean up content before saving
        this.componentData.content = this.$refs.editor.innerHTML
        this.$emit('update')
        this.updateToolbarState()

        this.$nextTick(() => {
          this.isUpdating = false
        })
      }, 10)
    },

    handleKeydown(event) {
      // Handle keyboard shortcuts
      if (event.ctrlKey || event.metaKey) {
        switch (event.key.toLowerCase()) {
          case 'b':
            event.preventDefault()
            this.execCommand('bold')
            break
          case 'i':
            event.preventDefault()
            this.execCommand('italic')
            break
          case 'u':
            event.preventDefault()
            this.execCommand('underline')
            break
          case 'k':
            event.preventDefault()
            this.toggleLink()
            break
        }
      }

      // Finish editing on Escape
      if (event.key === 'Escape') {
        this.finishEditing()
      }
    },

    handlePaste(event) {
      event.preventDefault()
      const text = event.clipboardData.getData('text/plain')
      document.execCommand('insertText', false, text)
    },

    handleFocus() {
      this.updateToolbarState()
    },

    handleBlur(event) {
      // Don't finish editing if clicking on toolbar
      if (event.relatedTarget && event.relatedTarget.closest('.rich-text-editor')) {
        return
      }
    },

    // Enhanced list toggling methods
    toggleBulletList() {
      this.$refs.editor.focus()

      // First, try the standard command
      document.execCommand('insertUnorderedList', false, null)

      // Then ensure proper styling
      this.$nextTick(() => {
        const lists = this.$refs.editor.querySelectorAll('ul')
        lists.forEach(list => {
          // Reset any problematic styles
          list.style.color = ''
          list.style.paddingLeft = '20px'
          list.style.marginTop = '8px'
          list.style.marginBottom = '8px'

          // Ensure list items inherit proper color
          const items = list.querySelectorAll('li')
          items.forEach(item => {
            if (!item.style.color || item.style.color === 'white') {
              item.style.color = 'inherit'
            }
          })
        })

        this.handleInput({ target: this.$refs.editor })
      })
    },

    toggleNumberedList() {
      this.$refs.editor.focus()

      // First, try the standard command
      document.execCommand('insertOrderedList', false, null)

      // Then ensure proper styling
      this.$nextTick(() => {
        const lists = this.$refs.editor.querySelectorAll('ol')
        lists.forEach(list => {
          // Reset any problematic styles
          list.style.color = ''
          list.style.paddingLeft = '20px'
          list.style.marginTop = '8px'
          list.style.marginBottom = '8px'

          // Ensure list items inherit proper color
          const items = list.querySelectorAll('li')
          items.forEach(item => {
            if (!item.style.color || item.style.color === 'white') {
              item.style.color = 'inherit'
            }
          })
        })

        this.handleInput({ target: this.$refs.editor })
      })
    },

    isListActive(listType) {
      const selection = window.getSelection()
      if (!selection.rangeCount) return false

      let node = selection.anchorNode
      while (node && node !== this.$refs.editor) {
        if (node.nodeType === 1) { // Element node
          if (listType === 'ul' && node.tagName === 'UL') return true
          if (listType === 'ol' && node.tagName === 'OL') return true
          if (node.tagName === 'LI') {
            const parent = node.parentElement
            if (listType === 'ul' && parent.tagName === 'UL') return true
            if (listType === 'ol' && parent.tagName === 'OL') return true
          }
        }
        node = node.parentElement
      }
      return false
    },

    // Rich text commands
    execCommand(command, value = null) {
      this.$refs.editor.focus()
      document.execCommand(command, false, value)
      this.updateToolbarState()
      this.componentData.content = this.$refs.editor.innerHTML
      this.$emit('update')
    },

    isCommandActive(command) {
      try {
        return document.queryCommandState(command)
      } catch (e) {
        return false
      }
    },

    updateToolbarState() {
      // Update current text color
      try {
        const color = document.queryCommandValue('foreColor')
        if (color) {
          this.currentTextColor = this.rgbToHex(color)
        }
      } catch (e) {
        // Handle command not supported
      }

      // Update current highlight color
      try {
        const bgColor = document.queryCommandValue('backColor')
        if (bgColor && bgColor !== 'transparent') {
          this.currentHighlightColor = this.rgbToHex(bgColor)
        }
      } catch (e) {
        // Handle command not supported
      }

      // Update font family
      try {
        const selection = window.getSelection()
        if (selection.rangeCount > 0) {
          let node = selection.anchorNode
          if (node.nodeType === 3) node = node.parentElement

          if (node && node !== this.$refs.editor) {
            const computedFont = window.getComputedStyle(node).fontFamily
            if (computedFont) {
              this.currentFontFamily = this.normalizeFontFamily(computedFont)
            }
          } else {
            // Use component default
            this.currentFontFamily = this.normalizeFontFamily(
              this.componentData.styles?.fontFamily || 'Arial, sans-serif'
            )
          }
        } else {
          // No selection, use component default
          this.currentFontFamily = this.normalizeFontFamily(
            this.componentData.styles?.fontFamily || 'Arial, sans-serif'
          )
        }
      } catch (e) {
        console.error('Error updating font family:', e)
        this.currentFontFamily = 'Arial, sans-serif'
      }

      // Update font size
      try {
        const selection = window.getSelection()
        if (selection.rangeCount > 0) {
          let node = selection.anchorNode
          if (node.nodeType === 3) node = node.parentElement

          if (node && node !== this.$refs.editor) {
            const computedSize = window.getComputedStyle(node).fontSize
            if (computedSize) {
              this.currentFontSize = computedSize
            }
          }
        }
      } catch (e) {
        // Handle command not supported
      }

      try {
        const selection = window.getSelection()
        if (selection.rangeCount > 0) {
          let node = selection.anchorNode
          if (node.nodeType === 3) node = node.parentElement

          if (node && node !== this.$refs.editor) {
            const computedLineHeight = window.getComputedStyle(node).lineHeight

            if (computedLineHeight) {
              if (computedLineHeight.includes('px')) {
                // Convert px to ratio
                const fontSize = parseFloat(window.getComputedStyle(node).fontSize)
                const lineHeightPx = parseFloat(computedLineHeight)
                const ratio = (lineHeightPx / fontSize)
                this.currentLineHeight = ratio.toFixed(2).replace(/\.?0+$/, '') // Remove trailing zeros
              } else if (computedLineHeight === 'normal') {
                this.currentLineHeight = '1.5'
              } else {
                // Already a unitless number
                this.currentLineHeight = parseFloat(computedLineHeight).toString()
              }
            }
          } else {
            // Use the component's style if no specific element
            this.currentLineHeight = this.componentData.styles?.lineHeight || '1.5'
          }
        } else {
          // No selection, use component default
          this.currentLineHeight = this.componentData.styles?.lineHeight || '1.5'
        }
      } catch (e) {
        console.error('Error updating line height state:', e)
        this.currentLineHeight = '1.5' // fallback
      }
    },

    // Text style (heading/paragraph)
    applyTextStyle(event) {
      const style = event.target.value
      if (style === 'p') {
        this.execCommand('formatBlock', 'p')
      } else {
        this.execCommand('formatBlock', style)
      }
    },

    // Font family
    applyFontFamily(event) {
      const fontFamily = event ? event.target.value : this.currentFontFamily

      // Apply to selection or entire editor
      this.$refs.editor.focus()

      // For multi-word fonts, we need to handle them carefully
      let fontNameForCommand = fontFamily

      // Extract just the primary font name for execCommand
      if (fontFamily.includes('Helvetica Neue')) {
        fontNameForCommand = 'Helvetica'
      } else if (fontFamily.includes('Times New Roman')) {
        fontNameForCommand = 'Times New Roman'
      } else if (fontFamily.includes('Courier New')) {
        fontNameForCommand = 'Courier New'
      } else if (fontFamily.includes('Comic Sans MS')) {
        fontNameForCommand = 'Comic Sans MS'
      } else if (fontFamily.includes('Trebuchet MS')) {
        fontNameForCommand = 'Trebuchet MS'
      } else {
        // Extract the first font name
        fontNameForCommand = fontFamily.split(',')[0].replace(/['"]/g, '').trim()
      }

      // Try using execCommand first
      document.execCommand('fontName', false, fontNameForCommand)

      // Apply the full font stack to selected elements
      const selection = window.getSelection()
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0)
        const container = range.commonAncestorContainer
        const element = container.nodeType === 3 ? container.parentElement : container

        if (element && element !== this.$refs.editor) {
          element.style.fontFamily = fontFamily
        }
      }

      // Also set it on the editor for new text
      this.$refs.editor.style.fontFamily = fontFamily

      // Update component data
      this.componentData.styles.fontFamily = fontFamily
      this.currentFontFamily = fontFamily

      this.handleInput({ target: this.$refs.editor })
    },

    // Font size
    applyFontSize(event) {
      const fontSize = event.target.value

      // Get a fresh selection from the editor
      this.$refs.editor.focus()
      const selection = window.getSelection()

      if (!selection.rangeCount) {
        return
      }

      const range = selection.getRangeAt(0)

      // Find the parent block element
      let node = range.commonAncestorContainer

      // A range on the editor itself spans several blocks, or starts at the very end of the
      // block above (dragging back past the line start): size every block it selects text in
      if (node === this.$refs.editor) {
        const blocks = this.getBlocksWithSelectedText(range)
        if (blocks.length > 0) {
          blocks.forEach(block => this.setBlockFontSize(block, fontSize))
          this.handleInput({ target: this.$refs.editor })
          this.currentFontSize = fontSize
          return
        }
      }

      // If the node is still the editor itself, get the first child or create a paragraph
      if (node === this.$refs.editor) {
        if (this.$refs.editor.children.length > 0) {
          node = this.$refs.editor.children[0]
        } else {
          // Create a paragraph if editor is empty or only has text nodes
          const p = document.createElement('p')
          p.innerHTML = this.$refs.editor.innerHTML || 'Start typing...'
          this.$refs.editor.innerHTML = ''
          this.$refs.editor.appendChild(p)
          node = p
        }
      }

      if (node.nodeType === 3) node = node.parentElement

      let parentBlock = node
      while (parentBlock && parentBlock !== this.$refs.editor) {
        const display = window.getComputedStyle(parentBlock).display
        if (display === 'block' || parentBlock.tagName.match(/^(P|DIV|H[1-6]|LI)$/)) {
          break
        }
        parentBlock = parentBlock.parentElement
      }

      // Apply font size to the parent block while preserving color
      if (parentBlock && parentBlock !== this.$refs.editor) {
        this.setBlockFontSize(parentBlock, fontSize)
      } else if (node && node !== this.$refs.editor) {
        // Fallback: apply to the node itself
        this.setBlockFontSize(node, fontSize)
      }

      this.handleInput({ target: this.$refs.editor })
      this.currentFontSize = fontSize
    },

    setBlockFontSize(element, fontSize) {
      const existingInlineColor = element.style.color
      element.style.fontSize = fontSize
      element.style.lineHeight = 'normal'
      // If no inline color is set, set the default from editor content color
      if (!existingInlineColor) {
        element.style.color = '#333333'
      }
    },

    // Top-level blocks the range selects at least one character of. A block the range
    // only touches at its edge (e.g. starts at the end of the line above) is skipped.
    getBlocksWithSelectedText(range) {
      return Array.from(this.$refs.editor.children).filter(block => {
        if (!range.intersectsNode(block)) return false
        const overlap = document.createRange()
        overlap.selectNodeContents(block)
        if (range.compareBoundaryPoints(Range.START_TO_START, overlap) > 0) {
          overlap.setStart(range.startContainer, range.startOffset)
        }
        if (range.compareBoundaryPoints(Range.END_TO_END, overlap) < 0) {
          overlap.setEnd(range.endContainer, range.endOffset)
        }
        return overlap.toString() !== ''
      })
    },

    // Color handling
    applyTextColor(color) {
      this.execCommand('foreColor', color)
      this.currentTextColor = color
      this.showColorPicker = false
    },

    // Highlight color
    applyHighlightColor(color) {
      if (color === 'transparent') {
        this.execCommand('backColor', 'transparent')
      } else {
        this.execCommand('hiliteColor', color)
      }
      this.currentHighlightColor = color
      this.showHighlightPicker = false
    },

    rgbToHex(rgb) {
      if (rgb.startsWith('#')) return rgb
      if (rgb === 'transparent') return 'transparent'

      const match = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/)
      if (!match) return '#333333'

      const hex = (x) => {
        const hex = parseInt(x).toString(16)
        return hex.length === 1 ? '0' + hex : hex
      }

      return '#' + hex(match[1]) + hex(match[2]) + hex(match[3])
    },

    // Link handling
    toggleLink() {
      const selection = window.getSelection()

      if (selection.rangeCount > 0) {
        this.savedSelection = selection.getRangeAt(0).cloneRange()

        let linkElement = selection.anchorNode
        while (linkElement && linkElement.tagName !== 'A') {
          linkElement = linkElement.parentElement
          if (linkElement === this.$refs.editor) {
            linkElement = null
            break
          }
        }

        if (linkElement && linkElement.tagName === 'A') {
          this.editingLink = true
          this.linkData.text = linkElement.textContent
          this.linkData.url = linkElement.href
          this.linkData.newTab = linkElement.target === '_blank'
        } else {
          this.editingLink = false
          this.linkData.text = selection.toString()
          this.linkData.url = ''
          this.linkData.newTab = true
        }

        this.modals.showLinkModal = true
      }
    },

    insertLink() {
      if (!this.savedSelection) return

      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(this.savedSelection)

      if (this.editingLink) {
        const linkElement = selection.anchorNode.parentElement
        if (linkElement && linkElement.tagName === 'A') {
          linkElement.href = this.linkData.url
          linkElement.textContent = this.linkData.text
          linkElement.target = this.linkData.newTab ? '_blank' : '_self'
        }
      } else {
        const linkHTML = `<a href="${this.linkData.url}" target="${this.linkData.newTab ? '_blank' : '_self'}">${this.linkData.text}</a>`
        document.execCommand('insertHTML', false, linkHTML)
      }

      this.closeLinkModal()
      this.handleInput({ target: this.$refs.editor })
    },

    removeLink() {
      this.execCommand('unlink')
    },

    closeLinkModal() {
      this.modals.showLinkModal = false
      this.editingLink = false
      this.linkData = { text: '', url: '', newTab: true }
      this.savedSelection = null
      this.$refs.editor.focus()
    }
  },

  mounted() {
    // Close dropdowns when clicking outside
    this.handleOutsideClick = (event) => {
      if (!this.$el.contains(event.target)) {
        this.showColorPicker = false
        this.showHighlightPicker = false
      }
    }
    document.addEventListener('click', this.handleOutsideClick)

    // If component is selected when first mounted, start editing
    if (this.selected) {
      this.startEditing()
    }
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
    if (this.inputTimeout) {
      clearTimeout(this.inputTimeout)
    }
  }
}
</script>

<style scoped>
.email-component.text-component {
  transition: all 0.2s ease;
  cursor: pointer;
}

.email-component.text-component.selected:not(.editing) {
  outline: 2px solid #e14eca;
  outline-offset: 2px;
}

.email-component.text-component.editing {
  outline: 2px solid #007bff;
  outline-offset: 2px;
  background: rgba(0, 123, 255, 0.05);
}

.component-content {
  padding: 8px 12px;
  word-wrap: break-word;
  white-space: pre-wrap;
  min-height: 40px;
}

.component-content:empty::before {
  content: "Click to edit text";
  color: #9ca3af;
  font-style: italic;
}

/* Rich Text Editor Styles */
.rich-text-editor {
  position: relative;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  flex-wrap: wrap;
}

.toolbar-select {
  padding: 6px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
  font-size: 13px;
  cursor: pointer;
  outline: none;
}

.toolbar-select:hover {
  border-color: #adb5bd;
}

.toolbar-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.style-select {
  min-width: 100px;
}

.font-select {
  min-width: 120px;
}

.size-select {
  min-width: 70px;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #495057;
  transition: all 0.2s;
  position: relative;
}

.toolbar-btn:hover {
  background: #e9ecef;
  border-color: #ced4da;
}

.toolbar-btn.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}


.color-indicator {
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 16px;
  height: 3px;
  border-radius: 1px;
  border: 1px solid rgba(0, 0, 0, 0.2);
}

.toolbar-divider {
  width: 1px;
  height: 24px;
  background: #dee2e6;
  margin: 0 4px;
}

.toolbar-group {
  display: flex;
  align-items: center;
  gap: 2px;
}

/* Editor Content */
.editor-content {
  min-height: 100px;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 4px 4px;
  background: white;
  outline: none;
  word-wrap: break-word;
  white-space: pre-wrap;
  /* Ensure default text color */
  color: #333333;
}

.editor-content:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.editor-content:empty::before {
  content: "Start typing...";
  color: #9ca3af;
  font-style: italic;
}

/* Enhanced editor content styles with explicit color inheritance */
.editor-content h1 {
  font-size: 32px;
  font-weight: bold;
  margin: 16px 0;
  color: inherit;
}
.editor-content h2 {
  font-size: 28px;
  font-weight: bold;
  margin: 14px 0;
  color: inherit;
}
.editor-content h3 {
  font-size: 24px;
  font-weight: bold;
  margin: 12px 0;
  color: inherit;
}
.editor-content h4 {
  font-size: 20px;
  font-weight: bold;
  margin: 10px 0;
  color: inherit;
}
.editor-content h5 {
  font-size: 18px;
  font-weight: bold;
  margin: 8px 0;
  color: inherit;
}
.editor-content h6 {
  font-size: 16px;
  font-weight: bold;
  margin: 8px 0;
  color: inherit;
}
.editor-content p {
  margin: 8px 0;
  color: inherit;
}

/* Critical: Explicit list styles to prevent white text */
.editor-content ul,
.editor-content ol {
  margin: 8px 0;
  padding-left: 20px;
  color: #333333 !important; /* Force color inheritance */
}

.editor-content li {
  margin: 4px 0;
  color: inherit !important; /* Ensure list items inherit proper color */
}

/* Prevent lists from becoming white in display mode too */
.component-content ul,
.component-content ol {
  margin: 8px 0;
  padding-left: 20px;
  color: inherit;
}

.component-content li {
  margin: 4px 0;
  color: inherit;
}

/* Override any potential white color on lists */
.editor-content ul[style*="color: white"],
.editor-content ol[style*="color: white"],
.editor-content ul[style*="color: rgb(255, 255, 255)"],
.editor-content ol[style*="color: rgb(255, 255, 255)"],
.editor-content li[style*="color: white"],
.editor-content li[style*="color: rgb(255, 255, 255)"] {
  color: #333333 !important;
}

.color-button-wrapper {
  position: relative;
}

/* Replace the existing color picker panel styles with: */
.color-picker-dropdown,
.highlight-picker-dropdown {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 12px;
  margin-top: 4px;
  min-width: 220px;
}

.color-presets {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
  width: 200px;
}

.color-preset {
  width: 32px;
  height: 32px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.color-preset:hover {
  transform: scale(1.1);
  border-color: #495057;
}

.color-preset i {
  color: white;
  font-size: 10px;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
}

.link-modal h4 {
  margin: 0 0 16px 0;
  color: #333;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  color: #495057;
}

.form-group input[type="checkbox"] {
  margin-right: 8px;
}
/* Links in content */
.editor-content a {
  color: #007bff;
  text-decoration: underline;
}

.component-content a {
  color: #007bff;
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 768px) {
  .editor-toolbar {
    gap: 2px;
    padding: 6px;
  }

  .toolbar-btn {
    width: 28px;
    height: 28px;
  }

  .toolbar-select {
    font-size: 12px;
    padding: 4px 6px;
  }

  .style-select {
    min-width: 80px;
  }

  .font-select {
    min-width: 100px;
  }

  .size-select {
    min-width: 60px;
  }
}

.line-height-select {
  min-width: 70px;
}

/* Toolbar Control Groups */
.toolbar-control-group {
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
}

/* Toolbar Labels */
.toolbar-label {
  font-size: 10px;
  text-transform: uppercase;
  color: #6c757d;
  font-weight: 600;
  letter-spacing: 0.5px;
  white-space: nowrap;
  user-select: none;
}

/* Toolbar Icons */
.toolbar-icon {
  color: #6c757d;
  font-size: 12px;
  width: 16px;
  text-align: center;
}

/* Adjust select styling when in a group */
.toolbar-control-group .toolbar-select {
  min-width: auto;
}

.toolbar-control-group .size-select {
  width: 70px;
}

.toolbar-control-group .line-height-select {
  width: 65px;
}

/* Add visual distinction with background */
.toolbar-control-group {
  background: rgba(255, 255, 255, 0.05);
  padding: 4px 6px;
  border-radius: 4px;
}

.toolbar-control-group:hover {
  background: rgba(255, 255, 255, 0.08);
}

/* Alternative: Add colored accent to distinguish */
.toolbar-control-group:nth-of-type(1) .toolbar-label {
  color: #007bff; /* Blue for font size */
}

.toolbar-control-group:nth-of-type(2) .toolbar-label {
  color: #28a745; /* Green for line height */
}

/* Mobile responsive */
@media (max-width: 768px) {
  .toolbar-label {
    font-size: 9px;
  }

  .toolbar-control-group {
    padding: 2px 4px;
  }
}
</style>
